<template>
  <sample-modal
    overlay-class="modal-opacity-bg overflow-auto"
    container-class="rounded-20 max-h-fill overflow-y-auto"
    @close-modal="closeModal"
  >
    <template #main>
      <div
        class="sm:h-auto max-h-fill w-screen sm:w-730px flex flex-col sm:rounded-20 py-4 px-6 bg-woodsmoke1 justify-start overflow-y-auto text-14"
      >
        <div class="flex sm:flex-row mt-2 flex-col-reverse items-center">
          <DialogTitle
            as="h3"
            class="text-20 font-druk-wide text-grayscale-96 sm:mt-0 mt-4"
          >
            Manage your track
          </DialogTitle>
          <button
            id="close-button"
            class="cursor-pointer w-6 h-6 flex items-center justify-center ml-auto"
            @click="closeModal"
          >
            <i class="icon-close text-24 text-grayscale-40" />
          </button>
        </div>
        <div class="mt-8" v-if="isTrackLink">
          <p
            class="text-start text-sm max-w-full overflow-ellipsis whitespace-nowrap overflow-hidden text-grayscale-40"
          >
            {{ editedTrack.link }}
          </p>
          <div
            v-if="previewUrl && !isPrivate"
            class="bg-card-background mt-6 rounded-12"
          >
            <iframe
              class="w-full h-20 rounded-12"
              :src="previewUrl"
              frameborder="0"
              allowtransparency="true"
              allow="encrypted-media"
            ></iframe>
          </div>
          <a
            v-if="isPrivate"
            :href="previewUrl"
            target="_blank"
            class="bg-card-background mb-2 p-4 rounded-8 w-full flex items-center justify-between gap-4"
          >
            <img class="w-12 h-12" :src="thumbnailUrl" alt="thumbnail" />
            <div
              class="truncate whitespace-nowrap overflow-hidden flex flex-col items-start"
            >
              <p class="font-bold">{{ title }}</p>
              <a :href="previewUrl">{{ previewUrl }}</a>
            </div>
            <i class="icon-play" />
          </a>
        </div>
        <div class="mt-8 text-grayscale-80" v-else>
          <p
            class="text-start text-sm max-w-full overflow-ellipsis whitespace-nowrap overflow-hidden text-grayscale-40"
          >
            {{ editedTrack.title }}
          </p>
          <div class="w-full rounded-4">
            <div class="bg-grayscale-13 px-4 py-3 mb-4 rounded-8">
              <ClientOnly>
                <AudioPreview
                  show-download
                  :file="editedTrack.link"
                  v-if="editedTrack.link"
                  :key="editedTrack.link"
                />
              </ClientOnly>
            </div>
            <div class="mt-6">
              <p class="flex flex-row text-grayscale-64 mb-2">
                Track name <span class="text-accent-magenta">*</span>
              </p>

              <input-field
                v-model:value="editedTrack.title"
                name="title"
                placeholder="Track title"
                type="text"
                bg-color="bg-grayscale-10"
                v-model:error-message="errors.titleError"
                @clearError="clearError"
                @proceed="checkInput"
              />
            </div>
          </div>
        </div>

        <div class="text-left mt-6">
          <p class="font-medium text-sm text-grayscale-60">
            What did you do on this track?
          </p>

          <input-field
            v-model:value="editedTrack.description"
            name="description"
            type="textarea"
            placeholder="Produce, write, perform, promote..."
            :is-disabled="isLoading"
            label-class="font-14 text-manatee"
            full-container-class="w-full"
            bg-color="bg-grayscale-10"
            :max-length="200"
            v-model:error-message="errors.descriptionError"
          />
        </div>

        <div
          :class="`${containerClass} mt-16 flex flex-col items-center sm:justify-between sm:flex-row`"
        >
          <button
            class="mt-8 p-1.5 w-84 h-46 text-grayscale-40 flex flex-row items-center justify-end order-last sm:order-first sm:mt-0 sm:mb-16"
            @click="confirmDelete = true"
          >
            <i class="text-20 leading-5 mr-2 text-grayscale-40 icon-trash" />
            <span>Delete</span>
          </button>

          <div
            class="w-full flex justify-start items-center flex-col sm:flex-row sm:w-273 sm:mb-16"
          >
            <button
              class="bg-grayscale-16 w-full rounded-54 h-46 text-base text-grayscale-96 font-bold ease-out duration-50 order-last sm:order-first sm:mr-6"
              @click="closeModal"
            >
              <span class="px-8 py-2">Cancel</span>
            </button>

            <button
              class="mb-4 bg-accent-orange w-full rounded-54 h-46 text-base text-grayscale-7 font-bold hover:bg-accent-orange-hover ease-out duration-50 sm:mb-0"
              :disabled="isLoading"
              @click="checkInput"
            >
              <span class="px-8 py-2">Update</span>
            </button>
          </div>
        </div>
      </div>
    </template>
  </sample-modal>
  <ConfirmationModal
    v-if="confirmDelete"
    title="Are you sure you want to delete this track?"
    description="We will remove this track from your list of tracks, however, it will remain attached to all requests you have made up to this point."
    confirm-title="Delete"
    @close="confirmDelete = false"
    @confirm="onDelete"
  />
</template>

<script setup lang="ts">
import InputField from "@/components/layout/inputs/InputField.vue";
import SampleModal from "@/components/modal/SampleModal.vue";
import ConfirmationModal from "../components/feedback/pro/components/ConfirmationModal.vue";
import { useTrackStore } from "@/store/track";
import { useLocalNotificationsStore } from "@/store/notifications";
import ApiError from "@/types/error";
import { TrackError, Track } from "@/types/track";
import { scrollToFirstError } from "@/utils/helpers";
import { validateField } from "@/utils/validation";
import { DialogTitle } from "@headlessui/vue";
import { computed, onMounted, PropType, reactive, ref } from "vue";
import AudioPreview from "../components/feedback/AudioPreview.vue";

const props = defineProps({
  track: {
    type: Object as PropType<Track>,
    required: true,
  },
});

const emit = defineEmits(["close"]);

const editedTrack = ref({ ...props.track });

const isTrackLink = computed(() =>
  editedTrack.value.provider === "upload" ? false : true,
);

const userAgent = navigator.userAgent || navigator.vendor;
const containerClass = computed(() => {
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    const chromeAgent =
      userAgent.indexOf("Chrome") > -1 || /Google Inc/.test(navigator.vendor);
    let safariAgent =
      userAgent.indexOf("Safari") > -1 &&
      /Apple Computer/.test(navigator.vendor);
    if (chromeAgent && safariAgent) safariAgent = false;
    return safariAgent ? "mb-24" : "";
  } else {
    return "";
  }
});

const trackStore = useTrackStore();
const localNotifications = useLocalNotificationsStore();

const confirmDelete = ref(false);

const preview = computed(() => trackStore.preview);
const previewUrl = ref("");
const isPrivate = ref(false);
const thumbnailUrl = ref("");
const title = ref("");

const isLoading = ref(false);
const errors = reactive({
  titleError: "",
  descriptionError: "",
} as TrackError);

onMounted(async () => {
  try {
    if (editedTrack.value.provider !== "upload") {
      await trackStore.getPreview(editedTrack.value.link);
      previewUrl.value = preview.value.previewUrl || "";
      title.value = preview.value.title || "";

      thumbnailUrl.value = preview.value.thumbnailUrl || "";
      isPrivate.value = preview.value.isPrivate || false;
    }
  } catch (error) {
    console.error(error);
  }
});

const closeModal = () => emit("close");

const checkInput = async (): Promise<void> => {
  isLoading.value = true;

  editedTrack.value.description = editedTrack.value.description.trim();
  editedTrack.value.title = editedTrack.value.title.trim();

  errors.titleError = validateField(editedTrack.value.title, "title", "input");
  errors.descriptionError = validateField(
    editedTrack.value.description,
    "description",
    "input",
  );
  if (errors.titleError || errors.descriptionError) {
    scrollToFirstError(errors);
    isLoading.value = false;
    return;
  }

  try {
    const track = await trackStore.editTrack(
      editedTrack.value._id,
      editedTrack.value.title,
      editedTrack.value.description,
    );
    closeModal();
  } catch (error) {
    const errorMessage = (error as ApiError).message;
    localNotifications.addNotification({
      message: errorMessage,
      isError: true,
    });
  }

  isLoading.value = false;
};

const onDelete = async () => {
  try {
    await trackStore.removeTrack(props.track._id);
    confirmDelete.value = false;
    closeModal();
  } catch (error: any) {
    localNotifications.addNotification({
      message: error.message,
      isError: true,
    });
  }
};

const clearError = (inputName: keyof TrackError): void => {
  if (Object.prototype.hasOwnProperty.call(errors, `${inputName}Error`)) {
    (errors as any)[`${inputName}Error`] = "";
  }
};
</script>
